/**
 * /music-stand — pages from files: PDF through pdf.js, photos through
 * createImageBitmap. One shape for both (Source), so the stand does not care
 * whether the score was engraved or photographed on a phone.
 *
 * Import rules that matter later on stage:
 *   · photos are downscaled ONCE to 2400 px on the long side and stored as
 *     JPEG — a 12-Mpx phone photo is 48 MB decoded; a tablet shows ~2 Mpx of
 *     it; five such pages in memory would crash an older iPad;
 *   · EXIF rotation is applied on decode (from-image), so a page shot in
 *     portrait is not lying on its side;
 *   · PDFs are stored as they came — they are vectors and stay sharp at any
 *     zoom.
 */
import { contentBox, rowProfile, inkMask, naturalCompare } from './core.js';

let pdfjsP = null;

function loadScript(src) {
    return new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = () => res(); s.onerror = () => rej(new Error('script ' + src));
        document.head.appendChild(s);
    });
}

/** pdf.js v4 through the site's loader; v3 (classic script) where modules of v4 cannot run. */
export function loadPdfjs(cfg) {
    if (window.__pdfjsLib) return Promise.resolve(window.__pdfjsLib);
    if (pdfjsP) return pdfjsP;
    pdfjsP = (async () => {
        try {
            // The layout's door waits for the loader when it is late; v3 below only when
            // it really failed (it used to jump to v3 the moment the loader was not there yet).
            return await window.__pdfjs(cfg.pdfjs || []);
        } catch (e) {
            for (const s of cfg.pdfjs3 || []) {
                try {
                    await loadScript(s.js);
                    if (window.pdfjsLib) {
                        window.pdfjsLib.GlobalWorkerOptions.workerSrc = s.worker;
                        window.__pdfjsLib = window.pdfjsLib;
                        return window.pdfjsLib;
                    }
                } catch (_) { /* next mirror */ }
            }
            throw e;
        }
    })();
    pdfjsP.catch(() => { pdfjsP = null; });
    return pdfjsP;
}

const isPdf = (f) => /pdf/i.test(f.type || '') || /\.pdf$/i.test(f.name || '');
const isImage = (f) => /^image\//i.test(f.type || '') || /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif|tiff?)$/i.test(f.name || '');

export function splitFiles(files) {
    const pdfs = [], images = [], other = [];
    for (const f of files) (isPdf(f) ? pdfs : isImage(f) ? images : other).push(f);
    images.sort((a, b) => naturalCompare(a.name || '', b.name || ''));
    return { pdfs, images, other };
}

function canvasToBlob(c, type = 'image/jpeg', q = 0.9) {
    return new Promise((res, rej) => {
        if (c.convertToBlob) { c.convertToBlob({ type, quality: q }).then(res, rej); return; }
        c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), type, q);
    });
}

async function decodeImage(blob) {
    if (typeof createImageBitmap === 'function') {
        try { return await createImageBitmap(blob, { imageOrientation: 'from-image' }); }
        catch (_) {
            try { return await createImageBitmap(blob); } catch (_) { /* fall through to <img> */ }
        }
    }
    const url = URL.createObjectURL(blob);
    try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        await img.decode();
        return img;
    } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
}

const dimsOf = (b) => ({ w: b.width || b.naturalWidth, h: b.height || b.naturalHeight });

/** A photographed page, ready to store: upright, at most 2400 px long. */
export async function prepareImage(file) {
    const bmp = await decodeImage(file);
    const { w, h } = dimsOf(bmp);
    if (!w || !h) throw new Error('empty image');
    const k = Math.min(1, 2400 / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    const x = c.getContext('2d');
    x.fillStyle = '#fff'; x.fillRect(0, 0, cw, ch);
    x.drawImage(bmp, 0, 0, cw, ch);
    if (bmp.close) bmp.close();
    return { blob: await canvasToBlob(c, 'image/jpeg', 0.9), w: cw, h: ch };
}

/** A small first-page picture for the library list. */
export async function thumbOf(drawable, w, h) {
    const tw = 120, th = Math.max(1, Math.round(120 * h / w));
    const c = document.createElement('canvas');
    c.width = tw; c.height = Math.min(th, 200);
    const x = c.getContext('2d');
    x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
    x.drawImage(drawable, 0, 0, tw, th);
    return canvasToBlob(c, 'image/jpeg', 0.8);
}

/* ───────────────────────── sources ───────────────────────── */

const MAX_PIXELS = 6e6;   // one rendered page — iOS keeps the total canvas memory tight (~24 MB each)

function blankCanvas(w, h) {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
}

async function pdfSource(blob, cfg) {
    const lib = await loadPdfjs(cfg);
    const data = new Uint8Array(await blob.arrayBuffer());
    const task = lib.getDocument({ data, isEvalSupported: false });
    const doc = await task.promise;
    const sizes = new Map();
    const sizeOf = async (i) => {
        if (sizes.has(i)) return sizes.get(i);
        const pg = await doc.getPage(i + 1);
        const vp = pg.getViewport({ scale: 1 });
        const s = { pw: vp.width, ph: vp.height };
        sizes.set(i, s);
        return s;
    };
    return {
        kind: 'pdf',
        pages: doc.numPages,
        sizeOf,
        knownSize: (i) => sizes.get(i) || null,
        async render(i, pxW) {
            const pg = await doc.getPage(i + 1);
            const vp1 = pg.getViewport({ scale: 1 });
            let scale = pxW / vp1.width;
            if (vp1.width * scale * vp1.height * scale > MAX_PIXELS) scale = Math.sqrt(MAX_PIXELS / (vp1.width * vp1.height));
            const vp = pg.getViewport({ scale });
            const c = blankCanvas(Math.max(1, Math.floor(vp.width)), Math.max(1, Math.floor(vp.height)));
            const x = c.getContext('2d', { alpha: false });
            x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
            await pg.render({ canvasContext: x, viewport: vp }).promise;
            return c;
        },
        close() { try { doc.destroy(); } catch (_) { /* already gone */ } },
    };
}

async function imageSource(blobs) {
    const cache = new Map();
    const sizes = new Map();
    const bmpOf = async (i) => {
        if (cache.has(i)) return cache.get(i);
        const b = await decodeImage(blobs[i]);
        cache.set(i, b);
        const { w, h } = dimsOf(b);
        sizes.set(i, { pw: w, ph: h });
        // a handful decoded at a time
        if (cache.size > 5) {
            const first = cache.keys().next().value;
            const old = cache.get(first); cache.delete(first);
            if (old && old.close) try { old.close(); } catch (_) { /* gone */ }
        }
        return b;
    };
    return {
        kind: 'images',
        pages: blobs.length,
        async sizeOf(i) { if (sizes.has(i)) return sizes.get(i); await bmpOf(i); return sizes.get(i); },
        knownSize: (i) => sizes.get(i) || null,
        async render(i, pxW) {
            const b = await bmpOf(i);
            const { w, h } = dimsOf(b);
            const k = Math.min(1, pxW / w);
            const c = blankCanvas(Math.max(1, Math.round(w * k)), Math.max(1, Math.round(h * k)));
            const x = c.getContext('2d', { alpha: false });
            x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
            x.drawImage(b, 0, 0, c.width, c.height);
            return c;
        },
        close() { for (const b of cache.values()) if (b && b.close) try { b.close(); } catch (_) { /* gone */ } cache.clear(); },
    };
}

export async function openSource(piece, fileRecs, cfg) {
    const blobs = fileRecs.slice().sort((a, b) => a.idx - b.idx).map((r) => r.blob);
    if (piece.kind === 'pdf') return pdfSource(blobs[0], cfg);
    return imageSource(blobs);
}

/** Where the music is on page i and where its systems are (a quick 640-px look). */
export async function analyzePage(src, i) {
    const c = await src.render(i, 640);
    const x = c.getContext('2d', { willReadFrequently: true });
    const img = x.getImageData(0, 0, c.width, c.height);
    const m = inkMask(img);
    const box = contentBox(m);
    const prof = rowProfile(m, box.full ? null : box, 512);
    const size = await src.sizeOf(i);
    return { box, prof, pw: size.pw, ph: size.ph, lowres: c };
}

/** Count pages of a PDF and draw its first page small, at import. */
export async function describePdf(blob, cfg) {
    const src = await pdfSource(blob, cfg);
    try {
        const c = await src.render(0, 240);
        const thumb = await thumbOf(c, c.width, c.height);
        return { pages: src.pages, thumb };
    } finally { src.close(); }
}
